'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { type ReactNode, useMemo } from 'react';
import { HOME_PAGE } from '@/app/constants';
import { cn, reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { Button } from '../ui/button';
import { Carousel } from './Carousel';

export interface StepItem {
  index: number;
  title: string;
  description: string;
  image?: string;
  alt?: string;
}

export interface ProductStepsProps {
  title: string;
  items: StepItem[];
  buttonText?: string;
  buttonClassName?: string;
  page?: string;
  className?: string;
  theme?: 'white' | 'dark';
  showButton?: boolean;
  columns?: 3 | 4;
  variant?: 'card' | 'plain';
  redirectUrl?: string;
  /** 可选：渲染在标题下方、卡片网格上方的额外内容（如按钮）。 */
  headerExtra?: ReactNode;
  /** 可选：覆盖内层容器（标题+卡片）的 padding 等样式。 */
  contentClassName?: string;
}

interface StepCardProps extends StepItem {
  theme: 'white' | 'dark';
  variant: 'card' | 'plain';
}

function StepCard({ title, description, image, index, theme, variant, alt }: StepCardProps) {
  const isDark = theme === 'dark';
  const isPlain = variant === 'plain';

  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-[24px] md:h-full',
        isPlain ? 'bg-transparent text-white' : isDark ? 'bg-system-white10 text-white' : 'bg-white text-black',
      )}
    >
      <div className="w-full">
        {image ? (
          isPlain ? (
            <img src={image} alt={alt || title} loading="lazy" className="aspect-[4/3] w-full object-contain p-3" />
          ) : (
            <Image src={image} alt={alt || title} width={397} height={272} className="w-full rounded-t-lg" />
          )
        ) : (
          <div
            className={cn(
              'flex h-[200px] w-full items-center justify-center',
              isPlain
                ? 'border border-system-white08 bg-transparent'
                : isDark
                  ? 'rounded-t-lg bg-gray-900'
                  : 'rounded-t-lg bg-gray-100',
            )}
          >
            {!isPlain && (
              <span className={cn('font-bold text-6xl', isDark ? 'text-gray-700' : 'text-gray-300')}>{index}</span>
            )}
          </div>
        )}
      </div>
      <div
        className={cn('flex w-full flex-1 flex-col', isPlain ? 'items-center px-1 pt-6 md:pt-5' : 'p-[32px] md:p-6')}
      >
        {isPlain && (
          <span className="mb-3 inline-flex items-center rounded-8 bg-white px-2 py-0.5 font-medium text-[12px] text-black">
            STEP {index}
          </span>
        )}
        <h3
          className={cn(
            'mb-3 text-[28px] leading-[1.4] md:text-[24px]',
            isPlain ? 'text-center font-medium' : 'text-left font-semibold',
          )}
        >
          {title}
        </h3>
        <div className={cn('flex flex-1 gap-[20px]', isPlain ? 'justify-center' : 'justify-between')}>
          <p
            className={cn(
              'leading-[1.6]',
              isPlain ? 'text-center text-sm md:text-[13px]' : 'text-left text-base md:text-sm',
              isDark || isPlain ? 'text-white/60' : 'text-gray-600',
            )}
          >
            {description}
          </p>
          {!isPlain && (
            <span
              className={cn(
                'translate-x-1 translate-y-[32px] self-end font-bold text-[#e4e4e4] text-[110px] leading-[1.4] sm:text-[90px]',
                isDark ? 'opacity-[0.08]' : 'opacity-100',
              )}
            >
              {index}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export function ProductSteps({
  title,
  items,
  buttonText,
  buttonClassName,
  page = 'ai-text-to-video',
  className,
  theme = 'white',
  showButton = true,
  columns = 3,
  variant = 'card',
  redirectUrl = HOME_PAGE,
  headerExtra,
  contentClassName,
}: ProductStepsProps) {
  const router = useRouter();

  const handleButtonClick = () => {
    reportEvent('element_click', {
      element_name: 'generate-now',
      page,
    });
    router.push(redirectUrl);
  };

  const stepsContent = useMemo(() => {
    return items.map((item) => <StepCard key={item.index} {...item} theme={theme} variant={variant} />);
  }, [items, theme, variant]);

  const t = useTranslations('ai-text-to-video');

  return (
    <section className={cn('pt-16', theme === 'white' ? 'bg-white text-black' : 'bg-black text-white', className)}>
      <div className={cn('mx-auto max-w-[1440px] px-8 py-12 text-center md:px-4 md:py-8', contentClassName)}>
        <h2
          className={cn('font-medium text-[40px] leading-[1.4] md:text-[36px]', headerExtra ? 'mb-6' : 'mb-16 md:mb-8')}
        >
          {title}
        </h2>
        {headerExtra ? <div className="mb-16 flex justify-center md:mb-8">{headerExtra}</div> : null}
        <div
          className={cn(
            'grid md:hidden md:grid-cols-1',
            variant === 'plain' ? 'gap-[50px]' : 'gap-[25px]',
            columns === 4 ? 'grid-cols-4' : 'grid-cols-3',
          )}
        >
          {stepsContent}
        </div>
        <div className={cn('hidden md:block')}>
          <Carousel
            items={stepsContent}
            slideClassName="px-4"
            autoPlay={false}
            indicatorColor={theme === 'dark' ? 'bg-white/30' : 'bg-gray-300'}
            indicatorActiveColor={theme === 'dark' ? 'bg-white' : 'bg-black'}
          />
        </div>
        {showButton && (
          <Button
            className={cn(
              'mt-12 h-[68px] min-w-[224px] rounded-[100px] bg-[#0086FF] px-10 py-6 font-medium text-white text-xl shadow-lg transition-all hover:shadow-xl sm:h-14 sm:w-[195px] sm:py-5 sm:text-base md:mt-8',
              buttonClassName,
            )}
            onClick={handleButtonClick}
          >
            {buttonText ?? t('buttons.generateNow')}
          </Button>
        )}
      </div>
    </section>
  );
}
